import { CONFIGURED_DOCUMENT_ID, TRAINING_DIRECTORY, TRAINING_DOCUMENTS, TRAINING_RECORD_SEEDS } from '../data/records';
import {
  TResolvedTraining,
  TTrainingDocument,
  TTrainingEmployee,
  TTrainingRecord,
  TTrainingRecordSeed,
  TTrainingStatus,
} from '../types';

const DAY_MS = 24 * 60 * 60 * 1000;

// The live demo state from useTrainingStore that feeds into the matrix, so assigning
// and confirming the configured document's training shows up on the records page.
export type TTrainingDemoState = {
  isActive: boolean;
  assignedDueDate?: string;
  completedBy?: string;
  completedAt?: number;
};

export type TTrainingMatrix = {
  employees: TTrainingEmployee[];
  documents: TTrainingDocument[];
  records: TTrainingRecord[];
};

const offsetDate = (now: number, days: number) => new Date(now + days * DAY_MS).toISOString();

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);

// Employees from the org config (the assign-dialog audience) take precedence over the
// built-in directory entries with the same id.
const buildDirectory = (training: TResolvedTraining): TTrainingEmployee[] => {
  const directory = new Map(TRAINING_DIRECTORY.map((employee) => [employee.id, employee]));
  training.employees.forEach(({ id, name, function: fn }) => directory.set(id, { id, name, function: fn }));
  return Array.from(directory.values());
};

const buildDocuments = (training: TResolvedTraining): TTrainingDocument[] =>
  TRAINING_DOCUMENTS.map((document) =>
    document.id === CONFIGURED_DOCUMENT_ID
      ? {
          ...document,
          id: training.documentId,
          name: training.documentName,
          revision: training.revision,
          trainingMethod: training.trainingMethod,
          targetFunctions: training.targetFunctions,
        }
      : document
  );

// Overlays the assign/confirm demo on the configured document's seeds: an active
// assignment opens a current-revision training for the included employees, and a
// confirmation adds a completed record for the confirming user.
const buildSeeds = (
  training: TResolvedTraining,
  demo: TTrainingDemoState,
  now: number
): { seeds: TTrainingRecordSeed[]; extraEmployees: TTrainingEmployee[] } => {
  let seeds = TRAINING_RECORD_SEEDS.map((seed) =>
    seed.documentId === CONFIGURED_DOCUMENT_ID ? { ...seed, documentId: training.documentId } : seed
  );
  const extraEmployees: TTrainingEmployee[] = [];

  if (demo.isActive) {
    const dueDate = demo.assignedDueDate ? new Date(demo.assignedDueDate).getTime() : now + training.dueInDays * DAY_MS;
    const dueInDays = Math.ceil((dueDate - now) / DAY_MS);
    const assignedIds = new Set(training.employees.filter((employee) => employee.included).map(({ id }) => id));
    seeds = [
      ...seeds.filter((seed) => !(seed.documentId === training.documentId && assignedIds.has(seed.employeeId))),
      ...Array.from(assignedIds, (employeeId) => ({
        employeeId,
        documentId: training.documentId,
        revision: training.revision,
        dueInDays,
      })),
    ];
  }

  if (demo.completedBy) {
    const completedDaysAgo = demo.completedAt ? Math.max(0, Math.floor((now - demo.completedAt) / DAY_MS)) : 0;
    extraEmployees.push({ id: 'current-user', name: demo.completedBy, function: '-' });
    seeds.push({
      employeeId: 'current-user',
      documentId: training.documentId,
      revision: training.revision,
      completedDaysAgo,
    });
  }

  return { seeds, extraEmployees };
};

const getStatus = (seed: TTrainingRecordSeed, document: TTrainingDocument): TTrainingStatus => {
  if (seed.completedDaysAgo !== undefined) return seed.revision === document.revision ? 'completed' : 'outdated';
  return (seed.dueInDays ?? 0) < 0 ? 'overdue' : 'open';
};

export const buildTrainingMatrix = (
  training: TResolvedTraining,
  demo: TTrainingDemoState,
  now: number = Date.now()
): TTrainingMatrix => {
  const { seeds, extraEmployees } = buildSeeds(training, demo, now);
  const employees = [...buildDirectory(training), ...extraEmployees].sort(byName);
  const documents = buildDocuments(training).sort(byName);
  const employeesById = new Map(employees.map((employee) => [employee.id, employee]));
  const records: TTrainingRecord[] = [];

  documents.forEach((document) => {
    const documentSeeds = seeds.filter((seed) => seed.documentId === document.id);
    const recordedIds = new Set(documentSeeds.map((seed) => seed.employeeId));

    documentSeeds.forEach((seed) => {
      const employee = employeesById.get(seed.employeeId);
      if (!employee) return;
      records.push({
        employee,
        document,
        status: getStatus(seed, document),
        revision: seed.revision,
        completedAt: seed.completedDaysAgo !== undefined ? offsetDate(now, -seed.completedDaysAgo) : undefined,
        dueDate: seed.dueInDays !== undefined ? offsetDate(now, seed.dueInDays) : undefined,
      });
    });

    // Required by function but no training on record at all.
    employees
      .filter((employee) => !recordedIds.has(employee.id) && document.targetFunctions.includes(employee.function))
      .forEach((employee) => records.push({ employee, document, status: 'not-trained' }));
  });

  return { employees, documents, records };
};

// "Trained" means trained on the current revision; everything else is a gap.
export const isTrainedStatus = (status: TTrainingStatus) => status === 'completed';

export const STATUS_SORT_ORDER: Record<TTrainingStatus, number> = {
  overdue: 0,
  'not-trained': 1,
  outdated: 2,
  open: 3,
  completed: 4,
};

export const getRecordsForEmployee = (matrix: TTrainingMatrix, employeeId: string) =>
  matrix.records
    .filter((record) => record.employee.id === employeeId)
    .sort((a, b) => STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status] || byName(a.document, b.document));

export const getRecordsForDocument = (matrix: TTrainingMatrix, documentId: string) =>
  matrix.records
    .filter((record) => record.document.id === documentId)
    .sort((a, b) => STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status] || byName(a.employee, b.employee));
